import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import BatteryIncentives from "./batterIncentive";
import BatteryConfiguration from "./batteryConfiguration";
import BatteryControl from "./batteryControl";
import { AccordionItem } from "../AccordianItem";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import { useForm } from "@mantine/form";
import MGAAPI from "../../api/index";
import displayError from "../../constants/utils";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { Grid, Switch, TextInput, useMantineColorScheme } from "@mantine/core";
import { set } from "lodash";
import { formatInputValue, mgaRegex, responsiveCol, useAssessmentRouteParams, useCurrentDesignComponentLists } from "../../constants/helper";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { formatProjectNumberRange, getTranslation } from "../../utils/helpers";
import batteryJson from "../../components/help/helpCardMenu.json";
import { IconPercentage } from "@tabler/icons-react";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import { getErrorMessage } from "../../utils/errorUtils";
import ProjectNumberInput from "../common/ProjectNumberInput";

const MGA_API = new MGAAPI();
const LoadBattery = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { setLoadingComp } = props;
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const [, setComponentStatus] = useAtom(componentStatusAtom);
  const [projectComponents, setComponentDetails] = useAtom(componentsAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [, setProjectDetails] = useAtom(projectDetailsAtom);
  const accordianRef = useRef(null);
  const header2Ref = useRef(null);
  const header3Ref = useRef(null);
  const header4Ref = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const {projectId, designId, designVersionId} = useAssessmentRouteParams();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);  
  const numberFormat = projectDetails?.projectDTO?.numberFormat;
  const formatRange = (min, max) =>
    formatProjectNumberRange(min, max, numberFormat);
  const form = useForm({
    key: JSON.stringify(projectComponents?.battery),
    initialValues: {
      ...projectComponents?.battery,
      componentAttributes: {
        ...projectComponents?.battery?.componentAttributes,
        batteryParameters: {
          ...projectComponents?.battery?.componentAttributes?.batteryParameters,
          SOCMax: projectComponents?.battery?.componentAttributes
            ?.batteryParameters?.SOCMax
            ? projectComponents?.battery?.componentAttributes?.batteryParameters
                ?.SOCMax
            : 1,
          EfficiencyCharge: projectComponents?.battery?.componentAttributes
            ?.batteryParameters?.EfficiencyCharge
            ? projectComponents?.battery?.componentAttributes?.batteryParameters
                ?.EfficiencyCharge
            : 1,
            EfficiencyDischarge: projectComponents?.battery?.componentAttributes
              ?.batteryParameters?.EfficiencyDischarge
              ? projectComponents?.battery?.componentAttributes?.batteryParameters
                  ?.EfficiencyDischarge
              : 1,
        },
      },
      economicAttributes: {
        ...projectComponents?.battery?.economicAttributes,
        batteryParameters: {
          ...projectComponents?.battery?.economicAttributes?.batteryParameters,
          InflationRate:
            projectComponents?.battery?.economicAttributes?.batteryParameters
              ?.InflationRate || "2",
        },
      },
      uiAttributes: {
        ...projectComponents?.battery?.uiAttributes,
        batteryParameters: {
          ...projectComponents?.battery?.uiAttributes?.batteryParameters,
          annualChargeCycle:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.annualChargeCycle || "200",
          assetId:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.assetId || 0,
          chargingMethod:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.chargingMethod || "No Constraint",
        },
      },
    },
    validate: {
      componentAttributes: {
        batteryParameters: {
          CapaMax: (value) =>
            !value
              ? t("battery.validation.capaMax.required")
              : !mgaRegex?.oneToLakhWithDecimalRegex.test(value)
              ? t("battery.validation.capaMax.range", formatRange(1, 100000))
              : null,
          DegradationFactor: (value) =>
            value && !mgaRegex?.zeroToFiveWithDecimal?.test(value)
              ? t("battery.validation.degradationFactor.range", formatRange(0, 5))
              : null,
          EfficiencyCharge: (value) =>
            !value
              ? t("battery.validation.efficiencyCharge.required")
              : Number(value) < 1
                ? t("battery.validation.greaterThanOne")
              : value && !mgaRegex?.zeroToHundredWithDecimal.test(value)
                ? t("battery.validation.efficiencyCharge.range", formatRange(0, 100))
                : null,
          EfficiencyDischarge: (value) =>
            !value
              ? t("battery.validation.efficiencyDischarge.required")
              : Number(value) < 1
                ? t("battery.validation.greaterThanOne")
              : value && !mgaRegex?.zeroToHundredWithDecimal.test(value)
                ? t("battery.validation.efficiencyDischarge.range", formatRange(0, 100))
                : null,
          SOCMax: (value) =>
            value === ""
              ? t("battery.validation.socMax.required")
              : Number(value) < 1
                ? t("battery.validation.greaterThanOne")
                : !mgaRegex?.zeroToHundredWithDecimal.test(value)
                  ? t("battery.validation.socMax.range", formatRange(0, 100))
                  : null,
          SOCMin: (value) =>
            value === ""
              ? t("battery.validation.socMin.required")
              : Number(
                  form?.values?.componentAttributes?.batteryParameters?.SOCMax
                ) <= Number(value)
              ? t("battery.validation.socMin.lessThanMax")
              : !mgaRegex?.zeroToHundredWithDecimal.test(value)
              ? t("battery.validation.socMin.range", formatRange(0, 100))
              : null,
          MaximumCharge: (value) =>
            value === ""
              ? t("battery.validation.maxCharge.required")
              : !mgaRegex?.oneToLakhWithDecimalRegex.test(value)
              ? t("battery.validation.capacity.range", formatRange(1, 100000))
              : null,
          PowElecNominalDischarge: (value) =>
            value === ""
              ? t("battery.validation.powElecNominalDischarge.required")
              : !mgaRegex?.oneToLakhWithDecimalRegex.test(value)
              ? t("battery.validation.powElecNominalDischarge.range", formatRange(1, 100000))
              : null,
        },
      },
      economicAttributes: {
        batteryParameters: {
          LifetimeCycle: (value) =>
            value === ""
              ? t("battery.validation.lifetimeCycle.required")
              : !mgaRegex?.oneToTenPower9Regex.test(value)
              ? t("battery.validation.lifetimeCycle.range", formatRange(1, 1000000000))
              : null,
          CAPEX: (value) =>
            value === ""
              ? t("battery.validation.capex.required")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(value)
              ? t("battery.validation.capex.range", formatRange(0, 1000000000))
              : null,
          Replacement: (value) =>
            value === ""
              ? t("battery.validation.replacement.required")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(value)
              ? t("battery.validation.replacement.range", formatRange(0, 1000000000))
              : null,
          OM: (value) =>
            value === ""
              ? t("battery.validation.om.required")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(value)
              ? t("battery.validation.om.range", formatRange(0, 1000000000))
              : null,
          InflationRate: (value) =>
            value === ""
              ? t("battery.validation.inflation.required")
              : !mgaRegex?.minusNinetyNineToPlusNinetyNineRegexWithDecimal.test(value)
              ? t("battery.validation.inflation.range", formatRange(-99, 999))
              : null,
        },
      },
      uiAttributes: {
        batteryParameters: {
          // selectBattery: (value) => (value ? null : "Battery is required"),
          assetId: (value) =>
            value === 0 || value ? null : t("battery.validation.assetId.required"),
          lifetimeInYears: (value) =>
            value === ""
              ? t("battery.validation.lifetimeYears.required")
              : !mgaRegex?.oneTo25Regex.test(value)
              ? t("battery.validation.lifetimeYears.range", formatRange(1, 25))
              : null,
          annualChargeCycle: (value) =>
            value === null ||
            value === undefined ||
            String(value).trim() === ""
              ? null
              : !mgaRegex?.oneToLakhRegex.test(String(value).trim())
              ? t("battery.validation.annualChargeCycle.range", formatRange(1, 100000))
              : null,
        },
      },
    },
  });

  useHelpFocusBlur(accordianRef, setHelperAtom, helperAtom);
  const handleContainerClick = (event, cardId) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector(`[card-id=${cardId}]`);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event, cardId) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event, cardId);
    }
  };

  const handleBatterySubmit = form.onSubmit(
    (values) => {
      handleBatterySave();
    },
    (errors) => {
      const firstErrorPath = Object.keys(errors)[0];
      const inputNode = form.getInputNode(firstErrorPath);
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  );

  useImperativeHandle(ref, () => ({
    handleBatterySubmit,
  }));

  const { setValues, reset } = form;
  // useEffect(() => {
  //   if (projectComponents?.battery) {
  //     reset({ ...projectComponents.battery }, { keepDefaultValues: false });
  //   }
  // }, [projectComponents.battery, reset]);

  useEffect(() => {
    if (!form.isTouched()) return;
    setComponentStatus((prev) => ({
      ...prev,
      battery: { isEditing: true, completed: false },
    }));
  }, [form.values, setComponentStatus]);

  const isControlEnabled =
    projectComponents?.battery?.uiAttributes?.batteryParameters
      ?.setupControlParameter ?? false;
  const isIncentivesEnabled =
    projectComponents?.battery?.uiAttributes?.batteryParameters
      ?.incentiveActive ?? false;

  const handleInputChange = (category, key, value) => {
    setComponentStatus((prev) => ({
      ...prev,
      battery: { isEditing: true, completed: false },
    }));
    setComponentDetails((prev) => {
      const newState = { ...prev, currentComponent: "battery" };
      if (category === "incentives") {
        set(newState, `battery.${category}`, key);
      } else {
        set(newState, `battery.${category}.batteryParameters.${key}`, value);
      }
      setValues(newState?.battery);
      return newState;
    });
  };

  const { colorScheme } = useMantineColorScheme();
  const [helpMenuData] = useAtom(helpContextMenuAtom);
    
  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.battery?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };

  const scrollToHeader2 = useCallback(() => {
    if (header2Ref.current) {
      header2Ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const scrollToHeader3 = useCallback(() => {
    if (header3Ref.current) {
      header3Ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);
  const scrollToHeader4 = useCallback(() => {
    if (header4Ref.current) {
      header4Ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const header1 = () => {
    return (
      <>
        <span className="stepText col-md-1 px-0">
          {" "}
          {`Step ${addedStepsSeries.indexOf("battery") + 1}`}: A.
        </span>
        <span
          className={`ml-2 col-md-8 px-0 ${
            colorScheme === "dark" ? "se-white" : "se-accordian-title"
          }`}
        >
          &nbsp;{t("battery.header.configure")}
        </span>
      </>
    );
  };

  const child1 = () => (
    <div
      data-id="accordion-battery-1"
      onClick={(e) => handleContainerClick(e, "accordion-battery-1")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-battery-1")}
      tabIndex={0}
      role="button"
    >
      <BatteryConfiguration
        handleInputChange={handleInputChange}
        form={form}
      />
    </div>
  );

  const header2 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("battery") + 1}`}: C.
      </span>
      <span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("battery.header.controlParameters")}
      </span>
    </>
  );

  const child2 = () => (
    <div
      data-id="accordion-battery-2"
      onClick={(e) => handleContainerClick(e, "accordion-battery-2")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-battery-2")}
      tabIndex={0}
      role="button"
    >
      {/* <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Switch
            {...getFieldAttributes("field-15")}
            data-id="field-15"
            label={getTranslation(
              "battery.form.addControlParams",
              "Add Control Parameters",
            )}
            color="seGreen.0"
            labelPosition="left"
            checked={
              projectComponents?.battery?.uiAttributes?.batteryParameters
                ?.setupControlParameter || false
            }
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              handleInputChange(
                "uiAttributes",
                "setupControlParameter",
                checked,
              );
              if (checked && typeof scrollToHeader2 === "function") {
                setTimeout(() => {
                  scrollToHeader2();
                }, 50);
              }
            }}
          />
        </Grid.Col>
      </Grid> */}

      {/* {isControlEnabled && ( */}
        <BatteryControl handleInputChange={handleInputChange} form={form} />
      {/* )} */}
    </div>
  );

  const header3 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("battery") + 1}`}: D.
      </span>
      <span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("battery.header.incentives")}
      </span>
    </>
  );

  const child3 = () => (
    <div
      {...getFieldAttributes("field-16")}
      data-id="field-16"
      onClick={(e) => handleContainerClick(e, "field-16")}
      onKeyDown={(e) => handleContainerKeyDown(e, "field-16")}
      tabIndex={0}
      role="button"
    >
      <BatteryIncentives
        handleInputChange={(data) => handleInputChange("incentives", data)}
        form={form}
      />
    </div>
  );

  const handleBatterySave = async () => {
    // --- Guard: do not proceed if battery incentive validation errors exist ---
    if (form?.errors?.batteryIncentives) {
      displayError("error", form.errors.batteryIncentives);
      return; // abort save
    }

    setLoadingComp(true);
    setComponentDetails((prevComponents) => ({
      ...prevComponents,
      isbatterySubmit: true,
    }));

    const filteredComponent = selectedDesignAndDesignVersion.componentDTOList.find(
      (comp) => comp.componentType === "Battery"
    );
    const payload = {
      projectId:
        projectId,
      designId:
        designId,
      designVersionId:
        designVersionId,
      componentType: "Battery",
      componentId: filteredComponent ? filteredComponent.componentId : -1,
      componentAttributes: {
        batteryParameters: {
          CapaMax:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.CapaMax,
          DegradationFactor:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.DegradationFactor,
          EfficiencyCharge:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.EfficiencyCharge,
          EfficiencyDischarge:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.EfficiencyDischarge,
          SOCMax:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.SOCMax,
          SOCMin:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.SOCMin,
          MaximumCharge:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.MaximumCharge,
          PowElecNominalDischarge:
            projectComponents?.battery?.componentAttributes?.batteryParameters
              ?.PowElecNominalDischarge,
        },
      },
      economicAttributes: {
        batteryParameters: {
          CAPEX:
            projectComponents?.battery?.economicAttributes?.batteryParameters
              ?.CAPEX,
          Replacement:
            projectComponents?.battery?.economicAttributes?.batteryParameters
              ?.Replacement,
          OM: projectComponents?.battery?.economicAttributes?.batteryParameters
            ?.OM,
          LifetimeCycle:
            projectComponents?.battery?.economicAttributes?.batteryParameters
              ?.LifetimeCycle,
          InflationRate:
            projectComponents?.battery?.economicAttributes?.batteryParameters
              ?.InflationRate,
        },
      },
      datasource: "OTHER",
      uiAttributes: {
        batteryParameters: {
          assetId:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.assetId ?? 0,
          selectBattery:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.selectBattery || "Custom",
          chargingMethod:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.chargingMethod || "Default",
          lifetimeInYears:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.lifetimeInYears || "",
          annualChargeCycle:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.annualChargeCycle,
          setupControlParameter:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.setupControlParameter ?? true,
          incentiveActive:
            projectComponents?.battery?.uiAttributes?.batteryParameters
              ?.incentiveActive ?? true,
        },
      },
      incentives: projectComponents.battery?.incentives || [],
    };
    MGA_API.saveLoadComponent(payload)
      .then((res) => {
        setComponentStatus((prev) => ({
          ...prev,
          battery: {
            completed: true,
            isEditing: false,
          },
        }));
        MGA_API.editProject(
          projectId
        ).then((res) => {
          setProjectDetails((prev) => ({
            ...prev,
            userId: res.userId || "",
            currencyCode: res?.projectDTO?.currencyCode || "$",
            projectDTO: res.projectDTO || {},
            componentDTOList:
              flattenCountComponentDTOList(res.countComponentDTOList) || [],
            designWithVersionsDTOList: res.designWithVersionsDTOList || [],
            locationDTO: res.locationDTO || {},
            countComponentDTOList: res.countComponentDTOList,
            designComponentsList: flattenDesignCountComponent(res?.designComponentsList) || [],
          }));
        }).catch((error) => {
          setLoadingComp(false);
          displayError("error", getErrorMessage(error));
        });
        setLoadingComp(false);
        displayError("success", t("battery.save.success"));
      })
      .catch((error) => {
        setLoadingComp(false);
        displayError('error', getErrorMessage(error));
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error);
      });
  };

    const header1_1 = () => {
    return (
      <>
        <span className="stepText col-md-1 px-0">
          {" "}
          {`Step ${addedStepsSeries.indexOf("battery") + 1}`}: B.
        </span>
        <span
          className={`ml-2 col-md-8 px-0 ${
            colorScheme === "dark" ? "se-white" : "se-accordian-title"
          }`}
        >
          &nbsp;{t("battery.header.investmentDetails", { defaultValue: 'Investment Details' })}
        </span>
      </>
    );
  };


  const children1_1 = () => (
     <div
      data-id="accordion-battery-2"
      onClick={(e) => handleContainerClick(e, "accordion-battery-2")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-battery-2")}
      tabIndex={0}
      role="button"
    >
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-11")}
            data-id={"field-11"}
            label={getTranslation(
              "battery.form.capex",
              "Capital Expenditure per kWh (One Time)"
            )}
            required
            value={
              projectComponents?.battery?.economicAttributes?.batteryParameters?.CAPEX ||
              ""
            }
            {...form.getInputProps(
              "economicAttributes.batteryParameters.CAPEX"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange("economicAttributes", "CAPEX", trimmedValue);
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight: "2.6rem",
                }}
              >
                {projectDetails?.projectDTO?.currencyCode || "$"}/kWh
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-12")}
            data-id={"field-12"}
            label={getTranslation(
              "battery.form.om",
              "Operation and Maintenance Cost (Yearly)"
            )}
            required
            placeholder={getTranslation(
              "battery.form.om.placeholder",
              "Operation and Maintenance"
            )}
            value={
              projectComponents?.battery?.economicAttributes?.batteryParameters?.OM || ""
            }
            {...form.getInputProps("economicAttributes.batteryParameters.OM")}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange("economicAttributes", "OM", trimmedValue);
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight: "4rem",
                }}
              >
                {projectDetails?.projectDTO?.currencyCode || "$"}/kWh/Year
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-13")}
            data-id={"field-13"}
            placeholder={getTranslation(
              "battery.form.replacement.placeholder",
              "Enter replacement cost"
            )}
            label={getTranslation(
              "battery.form.replacement",
              "Replacement Cost per kWh"
            )}
            required
            value={
              projectComponents?.battery?.economicAttributes?.batteryParameters
                ?.Replacement || ""
            }
            {...form.getInputProps(
              "economicAttributes.batteryParameters.Replacement"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange(
                "economicAttributes",
                "Replacement",
                trimmedValue
              );
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight: "2.6rem",
                }}
              >
                {projectDetails?.projectDTO?.currencyCode || "$"}/kWh
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
                  
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-9")}
            data-id={"field-9"}
            label={getTranslation("battery.form.lifetimeYears", "Lifetime In Years")}
            required
            placeholder={getTranslation(
              "battery.form.lifetimeYears.placeholder",
              "Enter value in years"
            )}
            value={
              projectComponents?.battery?.uiAttributes?.batteryParameters
                ?.lifetimeInYears || ""
            }
            {...form.getInputProps(
              "uiAttributes.batteryParameters.lifetimeInYears"
            )}
            onChange={(e) => {
              const trimmedValue = e.currentTarget.value
                .trim()
              handleInputChange(
                "uiAttributes",
                "lifetimeInYears",
                trimmedValue
              );
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight:"1rem"
                }}
              >
                Years
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            {...getFieldAttributes("field-14")}
            data-id={"field-14"}
            placeholder={getTranslation(
              "battery.form.inflation.placeholder",
              "Enter Inflation rate from -99 to 999"
            )}
            label={getTranslation(
              "battery.form.inflation",
              "Inflation Rate per year in %"
            )}
            required
            value={
              projectComponents?.battery?.economicAttributes?.batteryParameters
                ?.InflationRate || ""
            }
            {...form.getInputProps(
              "economicAttributes.batteryParameters.InflationRate"
            )}
            onChange={(e) => {
              const trimmedValue = e.currentTarget.value
                .trim()
              handleInputChange(
                "economicAttributes",
                "InflationRate",
                trimmedValue
              );
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                }}
              >
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
      </Grid>
    </div>
  )

  return (
    <div ref={accordianRef}>
      <AccordionItem id={1} header={header1()} key={1} children={child1()} />
      <AccordionItem
        id={2}
        header={header1_1()}
        key={2}
        children={children1_1()}
      />
      <AccordionItem id={3} header={header2()} key={3} children={child2()} />
      <AccordionItem
        id={4}
        height="fit-content"
        header={header3()}
        key={4}
        children={child3()}
      />
    </div>
  );
});

export default LoadBattery;
